import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { mdiCog, mdiCurrencyInr, mdiDotsHorizontal, mdiLayersTriple, mdiMonitorDashboard, mdiRobot } from "@mdi/js";
import { IoArrowBack, IoHeart, IoHeartOutline, IoInformationCircleOutline } from "react-icons/io5";
import Main from "../../components/layout/Main";
import AppSwitch from "../../components/layout/app/AppSwitch";
import AppModal from "../../components/layout/app/AppModal";
import MdiIcon from "../../components/device/app/MdiIcon";
import { deviceColors, deviceIcon } from "../../components/device/app/deviceTheme";
import { batteryLevel, sensorIconFlag } from "../../components/device/sensorStatus";
import { useToggleTop } from "../../components/common/ToggleTop";
import { useUser } from "../../providers/UserProvider";
import { UserService } from "../../services/UserService";
import { dimmerPayload, fanSpeedPayload, onOffPayload, rgbPayload, sendControl } from "../../services/deviceControl";
import { switchFeedback } from "../../preferences/feedback";
import { User } from "../../storage/store";
import TimeAgo from "../../helpers/TimeAgo";
import {
  BATTERY_MODELS,
  DEVICE_TYPES as T,
  METERING_PLUG_MODELS,
  GATE_TYPES,
  MODELS,
  SENSOR_TYPES,
  TRAITS,
  TRAIT_NAMES,
  hasTrait,
  hasTraitName,
  isModel,
  modelOf,
  typeOf,
} from "../../constants/devices";
import UsageHistory, { hasHistory, historyTitleOf } from "./detail/UsageHistory";
import DeviceInfo from "./detail/DeviceInfo";
import { CurtainControl, DimmerControl, FanRegulator, GateControl } from "./detail/Controls";
import WaterLevelControl, { motorsOf } from "./detail/WaterLevelControl";
import VacuumControl from "./detail/VacuumControl";
import { settingsPathFor } from "./settings/paths";
import SecurityAlarmControl from "./detail/SecurityAlarmControl";
import { ACCESSORY_PATHS } from "./security";
import EnergyMeterPanel from "./detail/EnergyMeterPanel";
import RgbControl, { hasRgbControls } from "./detail/RgbControl";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import { DeviceAutomationsPanel } from "../Automation/DeviceAutomations";
import { AUTOMATION_PATHS } from "../Automation/paths";
import "../../assets/styles/app-device.css";

// Types the app shows without an on/off switch in the title row.
const NO_SWITCH = [T.CURTAIN_MODULE, T.DIMMER_MODULE, T.ROBOT_VACUUM_CLEANER, T.SECURITY_ALARM, T.WATER_LEVEL_CONTROLLER, T.IRBM, ...GATE_TYPES];
// Types whose screen is always in their colour (the app forces them active).
const ALWAYS_FILLED = [T.DIMMER_MODULE, T.ROBOT_VACUUM_CLEANER, T.SECURITY_ALARM, ...GATE_TYPES];

// The usage screen behind the monitor icon: energy stats for plugs that
// measure power, run time for other switchable appliances.
const usagePathOf = (device) => {
  if (METERING_PLUG_MODELS.includes(modelOf(device))) return "/energy-stats";
  if ([T.SWITCH, T.LIGHT, T.FAN, T.SOCKET].includes(typeOf(device))) return "/runtime";
  return null;
};

// The app reads a dimmer's state as its brightness and an energy meter's as a
// number where 1 is on.
const isOn = (device, props) => {
  if (typeOf(device) === T.DIMMER_MODULE) return Number(props?.state) > 0;
  if (typeOf(device) === T.ENERGY_METER) return Number(props?.state) === 1;
  return props?.state === true;
};

// A device's detail screen, ported from the app's CommonSwichDetails: name and
// switch, favourite / info / schedules / settings buttons, the device's own
// controls, and its usage history.
function DeviceDetail() {
  const location = useLocation();
  const navigate = useHistory();
  const toast = useToggleTop();
  const { setDevice, profileName } = useUser();
  const passed = location.state?.data;
  const [device, setLocal] = useState(passed ?? null);
  const [lastState, setLastState] = useState(null);
  const [on, setOn] = useState(isOn(passed, passed?.props));
  const [speed, setSpeed] = useState(passed?.props?.speed);
  const [favourite, setFavourite] = useState(Boolean(passed?.usersFavourite?.includes(User.getUser())));
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [motor, setMotor] = useState(passed ? motorsOf(passed)[0] : 1);
  // The history panel's tab: "history" or "automations" (homes with the Automation feature).
  // The Automations list puts "automations" in this page's history entry before it opens
  // an automation or adds one, so coming back shows that tab again.
  const keptPanel = location.state?.panel === "automations" ? "automations" : "history";
  const [panel, setPanel] = useState(keptPanel);
  const keptPanelRef = useRef(keptPanel);
  keptPanelRef.current = keptPanel;
  const panelRef = useRef(null);
  const { on: automationOn } = useAutomationFeature();
  const menuRef = useRef(null);
  // The device on screen and a pending refresh, so a late reply for another
  // device can't replace this one.
  const shownId = useRef(passed?.applianceId);
  const refreshTimer = useRef(null);
  shownId.current = passed?.applianceId;

  const load = useCallback(async () => {
    if (!passed) return;
    const propertyId = User.getPropertyId();
    const [config, last] = await Promise.allSettled([
      UserService.getConfig(propertyId),
      UserService.getLastStatebydeviceId(propertyId, passed.deviceId),
    ]);
    const latest = config.value?.devices?.find((item) => String(item.applianceId) === String(passed.applianceId)) ?? passed;
    if (passed.applianceId !== shownId.current) return;
    const ls = last.status === "fulfilled" ? last.value : null;
    const own = ls?.devices?.[passed.applianceId] ?? ls?.devices?.[passed.deviceId];
    const props = own ? { ...passed.props, ...own } : passed.props ?? {};
    const merged = { ...latest, props, build: ls?.build ?? null, firmwareVersion: ls?.firmwareVersion ?? null, rssi: ls?.rssi };
    setLocal(merged);
    setDevice(merged);
    setLastState(ls);
    setOn(isOn(merged, props));
    setSpeed(props.speed);
    setFavourite(Boolean(merged.usersFavourite?.includes(User.getUser())));
  }, [passed, setDevice]);

  // Show the device that was opened straight away, then refresh it.
  useEffect(() => {
    if (passed) {
      setDevice(passed);
      setLocal(passed);
      setLastState(null);
      setOn(isOn(passed, passed.props));
      setSpeed(passed.props?.speed);
      setFavourite(Boolean(passed.usersFavourite?.includes(User.getUser())));
      setMotor(motorsOf(passed)[0]);
      setPanel(keptPanelRef.current);
    }
    load();
    return () => clearTimeout(refreshTimer.current);
  }, [load, passed, setDevice]);

  // Close the more menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  if (!passed) return <Redirect to="/dashboard" />;

  const type = typeOf(device);
  const props = device.props ?? {};
  const role = profileName.find((house) => house.id === User.getPropertyId())?.role;
  const isSensorType = SENSOR_TYPES.includes(type);
  const showSwitch = !NO_SWITCH.includes(type) && (type !== T.ENERGY_METER || hasTrait(device, TRAITS.METER_RELAY));
  const motorsOn = type === T.WATER_LEVEL_CONTROLLER && motorsOf(device).some((m) => lastState?.devices?.[`${device.applianceId}-${m}`]?.state);
  const filled = ALWAYS_FILLED.includes(type) || on || motorsOn;
  const darkText = type === T.ENERGY_METER;
  const [c0, c1] = deviceColors(type, props.isClosed ?? null);
  const iconFlag = isSensorType ? sensorIconFlag(type, props) : on;
  const battery = BATTERY_MODELS.includes(modelOf(device)) ? batteryLevel(props, device) : null;
  const settingsPath = settingsPathFor(device);
  const openDoor = type === T.DW_SENSOR ? (props.isClosed === false ? "OPEN" : "CLOSED") : null;

  const send = async (payload, revert) => {
    setBusy(true);
    try {
      await sendControl(payload);
      return true;
    } catch (err) {
      revert?.();
      toast(`Couldn't reach ${device.name}. Please try again.`, "red");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const switchTo = async (next) => {
    if (type === T.ENERGY_METER && !next) {
      setConfirmOff(true);
      return;
    }
    setOn(next);
    switchFeedback(next);
    if (await send(onOffPayload({ ...device, props: { ...props, speed } }, next), () => setOn(!next))) {
      device.props = { ...props, state: next };
    }
  };

  const confirmTurnOff = async () => {
    setConfirmOff(false);
    setOn(false);
    switchFeedback(false);
    await send(onOffPayload(device, false), () => setOn(true));
  };

  const changeSpeed = async (value) => {
    const previous = speed;
    setSpeed(value);
    setOn(true);
    await send(fanSpeedPayload(device, value), () => setSpeed(previous));
  };

  const changeBrightness = (value) => send(dimmerPayload(device, value));

  // An RGB light's effect, colour or brightness; sending them switches it on.
  const changeRgb = async (settings) => {
    const payload = rgbPayload(device, settings);
    if (!payload || !(await send(payload))) return false;
    setOn(true);
    device.props = { ...props, state: true };
    return true;
  };

  const toggleFavourite = async () => {
    const next = !favourite;
    setFavourite(next);
    try {
      await UserService.userFav({ applianceId: device.applianceId, favourite: next }, User.getPropertyId());
      toast(next ? "Added to favourites" : "Removed from favourites", "green");
    } catch (err) {
      setFavourite(!next);
      toast("Couldn't update favourites. Please try again.", "red");
    }
  };

  const editDevice = () => {
    setMenuOpen(false);
    if (role === "guest") {
      toast("You have to be a owner to edit the device.", "red");
      return;
    }
    navigate.push("/editDevice", { data: device });
  };

  const refreshSoon = () => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(load, 1500);
  };

  // The robot button: the panel's Automations tab, or a page of the device's
  // automations when it has no panel (app/screens/TimelineView.js).
  const openAutomations = () => {
    if (!hasHistory(device)) {
      navigate.push(AUTOMATION_PATHS.DEVICE, { data: device });
      return;
    }
    setPanel("automations");
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const showAutomations = automationOn && panel === "automations";
  const panelTabs = (
    <div className="hx-panel-tabs" role="tablist" aria-label="Device panel">
      {[
        ["automations", "Automations"],
        ["history", historyTitleOf(device)],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={panel === key}
          className={`hx-panel-tab ${panel === key ? "is-active" : ""}`}
          onClick={() => setPanel(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const hero = () => {
    if (type === T.FAN && on && hasTraitName(device, TRAIT_NAMES.FAN_SPEED)) {
      return <FanRegulator speed={speed} onSpeed={changeSpeed} disabled={busy} />;
    }
    if (type === T.DIMMER_MODULE && hasTrait(device, TRAITS.DIMMER)) {
      return <DimmerControl value={Number(props.state) || 0} onCommit={changeBrightness} disabled={busy} />;
    }
    if (type === T.CURTAIN_MODULE) return <CurtainControl device={device} />;
    if (GATE_TYPES.includes(type)) return <GateControl device={device} />;
    if (type === T.WATER_LEVEL_CONTROLLER) return <WaterLevelControl device={device} lastState={lastState} onChanged={refreshSoon} />;
    if (type === T.ROBOT_VACUUM_CLEANER) return <VacuumControl device={device} lastState={lastState} onChanged={refreshSoon} />;
    if (type === T.SECURITY_ALARM) {
      return isModel(device, MODELS.WZLTEG_1) ? (
        <SecurityAlarmControl device={device} lastState={lastState} />
      ) : (
        <p className="hx-detail-note">This security system can be controlled from the HASHh Connect app.</p>
      );
    }
    if (type === T.ENERGY_METER) return <EnergyMeterPanel device={device} />;
    if (type === T.RGB_LIGHT && hasRgbControls(device)) {
      return <RgbControl device={device} icon={deviceIcon(type, iconFlag)} on={on} disabled={busy} onSend={changeRgb} />;
    }
    return (
      <div className="hx-detail-icon" aria-hidden="true">
        <MdiIcon path={deviceIcon(type, iconFlag)} size={200} />
      </div>
    );
  };

  const controlsAtTop = [T.WATER_LEVEL_CONTROLLER, T.ENERGY_METER, T.SECURITY_ALARM, T.ROBOT_VACUUM_CLEANER].includes(type);

  return (
    <Main header="none" bleed hideTabBar>
      <div className={`hx-detail ${filled ? "is-active" : ""} ${darkText ? "is-dark-text" : ""}`}>
        <span className="hx-detail-flood" aria-hidden="true" style={{ background: `linear-gradient(to bottom, ${c0}, ${c1})` }} />
        <div className={`hx-page hx-detail-grid ${hasHistory(device) ? "" : "is-single"}`}>
          <div>
            <div className="hx-detail-top">
              <button
                type="button"
                className="hx-round"
                aria-label="Back"
                onClick={() => (navigate.length > 1 ? navigate.goBack() : navigate.push("/dashboard"))}
              >
                <IoArrowBack />
              </button>
              <div ref={menuRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  className="hx-round"
                  aria-label="More options"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  <MdiIcon path={mdiDotsHorizontal} size={28} />
                </button>
                {menuOpen ? (
                  <div className="hx-pop" role="menu">
                    <button type="button" role="menuitem" onClick={editDevice}>
                      Edit Device
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="hx-detail-title-row">
              <h1 className="hx-detail-name">
                {device.name}
                {openDoor ? <small>({openDoor})</small> : null}
              </h1>
              {showSwitch ? (
                <AppSwitch
                  checked={on}
                  onChange={switchTo}
                  disabled={busy}
                  label={isSensorType ? `${device.name} armed` : `${device.name} power`}
                  knobColor={c0}
                  trackColor={type === T.ENERGY_METER ? "rgb(71, 177, 116)" : "#ffffff"}
                />
              ) : null}
            </div>
            <div className="hx-detail-sub">
              <span>{device.floor && device.room ? `${device.floor} | ${device.room}` : ""}</span>
              {props.timeStamp && type !== T.ROBOT_VACUUM_CLEANER ? <time dateTime={props.timeStamp}>{TimeAgo.getTimeAgo(props.timeStamp)}</time> : null}
            </div>

            <div className="hx-detail-actions">
              <button type="button" className="hx-round is-small" onClick={toggleFavourite} aria-pressed={favourite} aria-label={favourite ? "Remove from favourites" : "Add to favourites"}>
                {favourite ? <IoHeart style={{ color: "red" }} /> : <IoHeartOutline />}
              </button>
              <button type="button" className="hx-round is-small" onClick={() => setInfoOpen(true)} aria-label="Device information">
                <IoInformationCircleOutline />
              </button>
              {automationOn ? (
                <button type="button" className="hx-round is-small" aria-label="Automations" onClick={openAutomations}>
                  <MdiIcon path={mdiRobot} size={22} />
                </button>
              ) : null}
              {type === T.ENERGY_METER ? (
                <button type="button" className="hx-round is-small" aria-label="Expense" onClick={() => navigate.push("/energy-expense", { data: device })}>
                  <MdiIcon path={mdiCurrencyInr} size={22} />
                </button>
              ) : null}
              {usagePathOf(device) ? (
                <button type="button" className="hx-round is-small" aria-label="Usage statistics" onClick={() => navigate.push(usagePathOf(device), { data: device })}>
                  <MdiIcon path={mdiMonitorDashboard} size={22} />
                </button>
              ) : null}
              {settingsPath ? (
                <button type="button" className="hx-round is-small" aria-label="Device settings" onClick={() => navigate.push(settingsPath, { data: device })}>
                  <MdiIcon path={mdiCog} size={22} />
                </button>
              ) : null}
              {isModel(device, MODELS.WZLTEG_1) ? (
                <button type="button" className="hx-round is-small" aria-label="Accessories" onClick={() => navigate.push(ACCESSORY_PATHS.LIST, { data: device })}>
                  <MdiIcon path={mdiLayersTriple} size={22} />
                </button>
              ) : null}
              {battery ? (
                <span className="hx-battery" title={`Battery ${battery.label.toLowerCase()}`}>
                  <MdiIcon path={battery.path} size={22} />
                  Battery {battery.label.toLowerCase()}
                </span>
              ) : null}
            </div>

            {/* Keyed by device so controls start fresh for each one. */}
            <div key={device.applianceId} className={`hx-detail-hero ${controlsAtTop ? "is-top" : ""}`}>
              {hero()}
            </div>
          </div>

          {hasHistory(device) ? (
            <div ref={panelRef}>
              {showAutomations ? (
                <section className="hx-history" aria-label="Automations">
                  <div className="hx-history-handle" aria-hidden="true" />
                  <div className="hx-history-head">{panelTabs}</div>
                  <DeviceAutomationsPanel device={device} />
                </section>
              ) : type === T.WATER_LEVEL_CONTROLLER && motorsOf(device).length > 1 ? (
                <div className="hx-chips" role="tablist" aria-label="Motor history" style={{ margin: 0, padding: "0 4px 14px" }}>
                  {motorsOf(device).map((m) => (
                    <button key={m} type="button" role="tab" aria-selected={motor === m} className={`hx-chip ${motor === m ? "is-active" : ""}`} onClick={() => setMotor(m)}>
                      Motor {m}
                    </button>
                  ))}
                </div>
              ) : null}
              {showAutomations ? null : <UsageHistory device={device} motor={motor} tabs={automationOn ? panelTabs : undefined} />}
            </div>
          ) : null}
        </div>
      </div>

      <DeviceInfo open={infoOpen} onClose={() => setInfoOpen(false)} device={device} lastState={lastState} />
      <AppModal
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        title="Turn Off Device?"
        actions={
          <>
            <button type="button" className="hx-btn is-ghost" onClick={() => setConfirmOff(false)}>
              Cancel
            </button>
            <button type="button" className="hx-btn" style={{ background: "#f44336" }} onClick={confirmTurnOff}>
              Turn Off
            </button>
          </>
        }
      >
        <p className="hx-modal-text">Are you sure you want to turn off {device.name}?</p>
      </AppModal>
    </Main>
  );
}

export default DeviceDetail;
